import clsx from 'clsx';
import Link, { type LinkProps as NextLinkProps } from 'next/link';
import type React from 'react';
import styles from './TextButton.module.scss';

interface SharedProps {
  icon: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'no-bg' | 'brand' | 'critical';
  iconPosition?: 'start' | 'end';
  size?: 'sm' | 'md';
  children?: React.ReactNode;
  className?: string;
  testId?: string;
  noShrink?: boolean;
}

type TextButtonProps = SharedProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: undefined; // Disallow href for button
  };

type LinkProps = SharedProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement> &
  Pick<NextLinkProps, 'replace' | 'scroll' | 'prefetch' | 'locale'> & {
    href: string;
  };

type Props = TextButtonProps | LinkProps;

export const TextButton: React.FC<Props> = (props) => {
  const {
    icon,
    variant = 'primary',
    iconPosition = 'end',
    size = 'md',
    children,
    className,
    testId,
    noShrink,
    ...rest
  } = props;

  const classNames = clsx(styles.button, className, {
    [styles.variantPrimary]: variant === 'primary',
    [styles.variantSecondary]: variant === 'secondary',
    [styles.variantNoBg]: variant === 'no-bg',
    [styles.variantBrand]: variant === 'brand',
    [styles.variantCritical]: variant === 'critical',
    [styles.sizeSm]: size === 'sm',
    [styles.sizeMd]: size === 'md',
    [styles.iconStart]: iconPosition === 'start',
    [styles.noShrink]: noShrink,
  });

  const content = (
    <>
      {children}
      <span className={styles.iconBackdrop} aria-hidden="true">
        {icon}
      </span>
    </>
  );

  if ('href' in props && props.href) {
    const { href, replace, scroll, prefetch, locale, ...anchorProps } = rest as LinkProps;
    return (
      <Link
        href={href}
        replace={replace}
        scroll={scroll}
        prefetch={prefetch}
        locale={locale}
        className={classNames}
        data-testid={testId}
        {...anchorProps}
      >
        {content}
      </Link>
    );
  }

  const { onClick, ...buttonProps } = rest as TextButtonProps;
  return (
    <button
      type={buttonProps.type || 'button'}
      onClick={onClick}
      className={classNames}
      data-testid={testId}
      {...buttonProps}
    >
      {content}
    </button>
  );
};
